import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  AirportDropDownList,
  DestinationType,
  UiStateManagementService
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import {
  DatePicker,
  DateRange,
  Dropdown,
  FromAndTo
} from '@com-delta-omnichannel/mach-core-js';
import { Subject } from 'rxjs';
import { VacationFormErrors } from '../models/vacation-form-data.interface';
import {
  VacationWidget,
  VacationWidgetFormType
} from '../models/vacation-widget.interfaces';
import { ComponentLifecycleService } from '../services/component-lifecycle.service';
import { ErrorHandlingService } from '../services/error-handling.service';
import { ErrorStateManagementService } from '../services/error-state-management.service';
import { VacationWidgetInitializationService } from '../services/vacation-widget-initialization.service';
@Component({
  selector: 'idp-vacation-widget-base',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './vacation-widget-base.component.html',
  styleUrls: ['./vacation-widget-base.component.scss']
})
export class VacationWidgetBaseComponent {
  @Input() content = {} as VacationWidget;
  @Input() vacationWidgetForm!: FormGroup<VacationWidgetFormType>;
  @Input() airportSelected: AirportDropDownList = { code: '', name: '' };
  @Input() viewSearchOptionExpanded = false;
  @Input() destinationType: DestinationType = '';
  @Input() closeModal = true;
  @Input() resetErrors = false;
  @Output() expandEmit = new EventEmitter<boolean>(false);
  @Output() openModalEmit = new EventEmitter<boolean>(false);
  @Output() formSubmitted = new EventEmitter();
  @Output() analyticsEvent = new EventEmitter<HTMLElement>();
  airportPicker!: FromAndTo;
  dropdownPassengerOptions!: Dropdown;
  dropdownRoomOptions!: Dropdown;
  rangeDatePickerInstance!: DatePicker;
  expandView = false;
  isIntentionalSubmit = false;
  isSubmitted = false;
  isDatePickerOpen = false;
  initializationService = inject(VacationWidgetInitializationService);
  uiStateService = inject(UiStateManagementService);
  lifecycleService = inject(ComponentLifecycleService);
  errorHandlingService = inject(ErrorHandlingService);
  errorStateService = inject(ErrorStateManagementService);
  elementRef = inject(ElementRef);
  destroy$ = new Subject<void>();
  eventService = this.lifecycleService.eventService;
  getError(errors: VacationFormErrors) {
    const result = this.errorHandlingService.processErrors(
      errors,
      this.vacationWidgetForm,
      this.elementRef.nativeElement
    );
    this.errorStateService.updateErrorState(errors, result);
  }

  initializeComponents(): void {
    Object.assign(
      this,
      this.lifecycleService.initializeWidget(
        this.content,
        this.vacationWidgetForm,
        () => this.isSubmitted,
        () => {
          this.isDatePickerOpen = false;
        }
      )
    );
  }

  updateFormDates(dates: DateRange): void {
    if (this.vacationWidgetForm) {
      this.vacationWidgetForm.patchValue({
        departDate: dates.start,
        returnDate: dates.end
      });
    }
  }

  updateFormAirport(airportCode: string): void {
    if (this.isSubmitted) {
      this.errorStateService.airportErrorMessage =
        this.lifecycleService.updateFormAirport(
          airportCode,
          this.destinationType,
          this.vacationWidgetForm,
          this.airportPicker,
          this.content
        );
    }
  }

  handleErrorCountChange(): void {
    if (this.isSubmitted) {
      const errors = this.lifecycleService.processFormSubmission(
        this.vacationWidgetForm,
        this.airportPicker,
        this.content
      );
      this.getError(errors);
    }
  }

  processFormSubmit(): void {
    this.isSubmitted = true;
    this.isIntentionalSubmit = false;
    const errors = this.lifecycleService.processFormSubmission(
      this.vacationWidgetForm,
      this.airportPicker,
      this.content
    );
    if (this.vacationWidgetForm.invalid) {
      this.getError(errors);
    }
    if (this.vacationWidgetForm.valid) {
      this.formSubmitted.emit();
    }
  }

  openDatePicker(): void {
    this.isDatePickerOpen = !this.isDatePickerOpen;
    this.initializationService.openDatePicker(this.rangeDatePickerInstance);
  }

  destroySubscriptions(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
